import React, { useEffect, useState } from 'react';
import {
  Collapse,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startCase, find, sortBy, get } from 'lodash';
import kebabcaseKeys from 'kebabcase-keys';
import { toast } from 'react-toastify';
import { setAccessToken, useFeatures, useSession } from '@cordata/buffet';

import {
  authorizedInstance as axios,
  authorizedPyInstance as py,
} from '~/utils';
import { deburrObject } from '~/utils/api/normalizers';

import Identity from '~/assets/identity.svg';
import PatientQuickSearch from '~/components/Patient/QuickSearch';
import routes from '~/utils/route/routes';
import LastRefresh from '../Patient/Identity/LastRefresh';

const CordataNavBar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState();
  const [currentDivision, setCurrentDivision] = useState({});
  const [organizations, setOrganizations] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const params = useParams();
  const features = useFeatures();
  const {
    hasAppNavigator, hasAppIdentity, hasDebugMode,
  } = useFeatures();
  const {
    current, currentAppUser, fetchAppUser, toggleDebugMode, debugMode,
  } = useSession();

  useEffect(() => {
    if (current) {
      fetchAppUser(current);

      py
        .get(`api/data/public/division/${current.attributes.currentDivisionUid}`)
        .then(r => deburrObject(r.data.data))
        .then(setCurrentDivision);
    }

    py
      .get('/destinations')
      .then(r => r.data.data)
      .then((r) => {
        console.log('Destinations:', r);

        setDestinations(r);
      });
  }, [current]);

  const quickCopy = () => {
    if (navigator.clipboard) {
      Promise.all([
        py.get(`api/data/public/division/${current.attributes.currentDivisionUid}`)
          .then((r) => r.data.data)
          .then(r => Promise.all([r,
            py.get(`api/data/public/organization/${r.attributes.orgUid}`),
          ]).then(([div, org]) => {
            return {
              ...div,
              relationships: {
                ...div.relationships,
                organization: org.data.data,
              },
            };
          })),
        fetch('/version.json')
          .then(r => r.json()),
        axios.get('/version')
          .then((r) => r.data),
        py.get('/version')
          .then((r) => r.data),
      ]).then((r) => {
        const [div, ui, api, python] = r;

        let env = 'prod';

        if (window.location.host.includes('dev')) {
          env = 'dev';
        } else if (window.location.host.includes('carbon')) {
          env = 'carbon';
        } else if (window.location.host.includes('staging')) {
          env = 'staging';
        }

        const message = `|page info|
|--|
|${window.location}|
|${window.navigator.userAgent}|
---
|env|ui|api ( rb )|api ( py )|db|
|--|--|--|--|--|
|${env}|${ui.version || 'latest'} ( \`${ui.commit}\` )|${api.version || 'latest'} ( \`${api.commit}\` )|${python.version || 'latest'} ( \`${python.commit}\` )|${api.db}|
---
|label|value|uid|
|--|--|--|
|User|${current.attributes.userName}|\`${current.attributes.userUid}\`|
|Organization|${div.relationships.organization.attributes.orgName}|\`${div.relationships.organization.id}\`|
|Division|${div.attributes.divisionName}|\`${div.id}\`|
${params && Object.keys(params).length > 0 ? `---
|key|value|
|--|--|
${Object.keys(params).map(k => `|${k}|\`${params[k]}\`|\n`)}
` : ''}`;

        navigator.clipboard.writeText(message);

        toast.info('Page info copied!');
      });
    }
  };

  const getDestination = (destinationName) => find(destinations, ['name', destinationName]);

  const goToDestination = (destinationName, route = '') => {
    const dest = getDestination(destinationName);

    if (dest) window.location = `${dest.url}${route}`;
  };

  const logout = () => {
    localStorage.clear();

    goToDestination('Gatekeeper', '/logout');
  };

  const toggle = () => setIsOpen(!isOpen);

  const attemptFetchUser = () => {
    axios
      .get('session/current')
      .then(r => r.data)
      .then((r) => {
        const c = JSON.stringify(r);
        localStorage.setItem('current', c);
      });
  };

  const handleUserProfile = () => goToDestination('Gatekeeper', '/user/user-profile');

  const handleAdmin = () => goToDestination('Navigator Admin', '/admin');

  const changeDivision = (d) => {
    const c = JSON.parse(localStorage.getItem('current')).data;

    c.relationships.currentDivision = { data: { type: 'divisions', id: d.divisionUid } };

    axios
      .put('/session/current', kebabcaseKeys({ data: c }, { deep: true }))
      .then(r => r.data.data.attributes)
      .then((r) => {
        console.log('Current:', r);

        localStorage.clear();

        setAccessToken(r.accessToken);

        goToDestination('Gatekeeper', '/home');
      })
      .catch(console.error);
  };

  const buildOrgsFromCurrent = curr => {
    const orgs = curr.included.filter(i => i.type === 'organizations');

    setOrganizations(sortBy(orgs, ['attributes.name']).map(o => ({
      ...o,
      relationships: {
        ...o.relationships,
        divisions: sortBy(
          o.relationships.divisions.data.filter(d =>
            current.attributes.accessibleDivisions
              .map(ad => ad.divisionUid)
              .includes(d.id))
            .map(d =>
              find(current.attributes.accessibleDivisions, ['divisionUid', d.id])),
          ['divisionName'],
        ),
      },
    })));
  };

  const gotoCordataWeb = () => {
    const url = process.env.REACT_APP_CORDATA_WEB_URL || 'http://localhost:4100';
    window.open(url, '_self');
  };

  useEffect(() => {
    setOrganizations([]);

    fetch('/version.json')
      .then(r => r.json())
      .then(r => console.table(r));

    const c = JSON.parse(localStorage.getItem('current'));

    setCurrentUser(c);

    if (!(c)) {
      attemptFetchUser();
    }

    if (currentAppUser && c && c.included) {
      buildOrgsFromCurrent(c);
    } else if (hasAppNavigator) {
      axios
        .get('session/current')
        .then(r => r.data)
        .then((r) => {
          const user = JSON.stringify(r);
          localStorage.setItem('current', user);

          buildOrgsFromCurrent(r);
        });
    }
  }, [localStorage.getItem('current'), currentAppUser]);

  return (
    <Navbar color={debugMode ? 'danger' : 'primary'} dark expand="md" className="sticky-top d-print-none">
      <NavbarBrand href="/">
        <img src={Identity} width={130} alt="Navigator - Cordata" className="mr-2" />
      </NavbarBrand>

      <NavbarToggler onClick={toggle} />

      <Collapse isOpen={isOpen} navbar>
        <Nav className="mr-auto" navbar>
          {routes.filter(r => r.main).filter((r) => {
            const flag = r.active;

            if (flag) {
              return Array.isArray(flag)
                ? flag.some(f => features[f])
                : features[flag];
            }

            return r;
          }).map(r => (
            <NavItem key={r.path} active={location.pathname.includes(r.path)}>
              <Link to={r.path} className="nav-link">
                <FontAwesomeIcon icon={r.icon} fixedWidth size="lg" />
                <span className="d-inline-block d-sm-inline-block d-md-none ml-2">{startCase(r.label)}</span>
              </Link>
            </NavItem>
          ))}

          {hasAppIdentity && <LastRefresh />}

          {hasAppNavigator && <PatientQuickSearch theme={{ color: 'light', size: 'sm', inline: true }} />}
        </Nav>

        <Nav className="ml-auto" navbar>
          {hasAppNavigator && (
            <UncontrolledDropdown nav inNavbar>
              <DropdownToggle nav caret>
                <FontAwesomeIcon icon="user-circle" fixedWidth size="lg" className="d-md-none" />

                <span className="d-inline-block d-md-inline-block mx-2">{organizations.length > 1 && `${get(find(organizations, ['id', currentDivision.orgUid]), 'attributes.name')} / `}{currentDivision.divisionName}</span>
                <FontAwesomeIcon icon="user-circle" fixedWidth size="lg" className="d-none d-md-inline-block" />
              </DropdownToggle>

              <DropdownMenu right style={{ maxHeight: '50vh', overflowY: 'scroll' }}>
                <DropdownItem header>
                  Logged in: {currentAppUser?.attributes?.displayName ||
                    currentAppUser?.attributes.userName}
                </DropdownItem>

                {organizations.map(o => (
                  <React.Fragment key={o.id}>
                    <DropdownItem divider />

                    <DropdownItem header>
                      {o.attributes.name}
                    </DropdownItem>

                    {o.relationships.divisions.map(d => (
                      <DropdownItem key={d.divisionUid} active={(currentUser || '{}').data && currentUser.data.relationships.currentDivision.id === d.divisionUid} onClick={() => changeDivision(d)}>
                        {d.divisionName}
                      </DropdownItem>
                    ))}
                  </React.Fragment>
                ))}

                <DropdownItem divider />

                <DropdownItem onClick={handleUserProfile}>
                  User Profile
                </DropdownItem>

                {getDestination('Navigator Admin') && (
                  <>
                    <DropdownItem divider />

                    <DropdownItem onClick={handleAdmin}>
                      Admin
                    </DropdownItem>
                  </>
                )}

                <DropdownItem divider />

                <DropdownItem className="text-primary" onClick={() => window.open('mailto:support@cordatahealth.com')}>
                  Help
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          )}

          {hasDebugMode && (
            <>
              <NavItem className="nav-link" onClick={toggleDebugMode}>
                <FontAwesomeIcon icon="bug" fixedWidth size="lg" />
              </NavItem>

              <NavItem className="nav-link" onClick={quickCopy}>
                <FontAwesomeIcon icon="copy" fixedWidth size="lg" />
              </NavItem>
            </>
          )}

          {debugMode && features.cordataWeb && (
            <>
              <UncontrolledTooltip target="cordata-web-icon">
                Cordata Web
              </UncontrolledTooltip>

              <NavItem className="nav-link" onClick={gotoCordataWeb}>
                <FontAwesomeIcon icon="star" fixedWidth size="lg" id="cordata-web-icon" />

                <span className="d-inline-block d-sm-inline-block d-md-none ml-2">Cordata Web</span>
              </NavItem>
            </>
          )}

          <NavItem className="nav-link" onClick={logout}>
            <FontAwesomeIcon icon="sign-out-alt" fixedWidth size="lg" />

            <span className="d-inline-block d-sm-inline-block d-md-none ml-2">Logout</span>
          </NavItem>
        </Nav>
      </Collapse>
    </Navbar>
  );
};

export default CordataNavBar;
